import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useBilling } from "@/context/BillingContext";
import { Activity, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const MAX_POLLS = 8;

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { refresh } = useBilling();
  const polled = useRef(false);
  const [state, setState] = useState("checking"); // checking | success | pending | error

  useEffect(() => {
    if (polled.current) return;
    polled.current = true;
    if (!sessionId) {
      navigate("/dashboard", { replace: true });
      return;
    }
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/api/billing/checkout/status/${sessionId}`);
        if (data?.entitlement?.access === "full" || data?.status === "complete") {
          await refresh();
          setState("success");
          toast.success("You're now on Pro — enjoy the full suite!");
          setTimeout(() => navigate("/dashboard", { replace: true }), 1800);
          return;
        }
      } catch {
        /* keep trying */
      }
      if (tries >= MAX_POLLS) {
        setState("pending");
        return;
      }
      setTimeout(poll, 1500);
    };
    poll();
  }, [sessionId, navigate, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F7F9] px-4" data-testid="billing-success">
      <div className="text-center max-w-sm">
        {state === "checking" && (
          <>
            <Activity className="w-8 h-8 text-[#0047AB] animate-pulse mx-auto mb-3" />
            <p className="text-sm text-slate-600">Confirming your subscription…</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
            <h1 className="text-2xl font-bold text-slate-900">Welcome to Pro!</h1>
            <p className="text-sm text-slate-600 mt-2">Taking you to your dashboard…</p>
          </>
        )}
        {state === "pending" && (
          <>
            <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-slate-900">Almost there</h1>
            <p className="text-sm text-slate-600 mt-2">
              Your payment is processing. Your plan will update shortly.
            </p>
            <Button onClick={() => navigate("/dashboard", { replace: true })} data-testid="billing-success-continue"
              className="mt-5 bg-[#0047AB] hover:bg-[#003380] text-white">
              Go to dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
